import React from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Building2,
  CalendarRange,
  FileText,
  GraduationCap,
  School,
  Shield,
  Store,
  Wallet,
} from "lucide-react";

export const money = (v) =>
  `${Number(v || 0).toLocaleString("vi-VN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })} ₫`;

export const dateVN = (v) => {
  const s = String(v || "").slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s || "—";
};

export function Badge({ children, tone = "gray", className = "" }) {
  const tones = {
    gray: "bg-gray-100 text-gray-600 border-gray-200",
    red: "bg-red-50 text-red-700 border-red-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    violet: "bg-violet-50 text-violet-700 border-violet-100",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${
        tones[tone] || tones.gray
      } ${className}`}
    >
      {children}
    </span>
  );
}

export function DirectionBadge({ direction }) {
  if (direction === "in") {
    return (
      <Badge tone="emerald">
        <ArrowDownCircle size={11} /> Thu
      </Badge>
    );
  }
  return (
    <Badge tone="red">
      <ArrowUpCircle size={11} /> Chi
    </Badge>
  );
}

export function EntryTypeBadge({ entryType, label }) {
  if (entryType === "receipt") {
    return (
      <Badge tone="blue">
        <FileText size={11} /> {label || "Phiếu thu"}
      </Badge>
    );
  }
  return (
    <Badge tone="slate">
      <Wallet size={11} /> {label || "Sổ quỹ"}
    </Badge>
  );
}

export function PayerTypeBadge({ payerType, label }) {
  const map = {
    student: { tone: "violet", icon: GraduationCap },
    school: { tone: "blue", icon: School },
    customer: { tone: "emerald", icon: Store },
    other: { tone: "slate", icon: Building2 },
  };
  const cfg = map[payerType] || map.other;
  const Icon = cfg.icon;
  return (
    <Badge tone={cfg.tone}>
      <Icon size={11} /> {label || payerType || "Khác"}
    </Badge>
  );
}

export function InvoiceStatusBadge({ status, label }) {
  if (status === "issued") {
    return <Badge tone="amber">{label || "Đã xuất"}</Badge>;
  }
  if (status === "recorded") {
    return <Badge tone="blue">{label || "Đã ghi nhận"}</Badge>;
  }
  return <Badge tone="gray">{label || status || "—"}</Badge>;
}

export function DaysOutstandingBadge({ days }) {
  const n = Number(days) || 0;
  let tone = "emerald";
  if (n >= 30) tone = "red";
  else if (n >= 14) tone = "amber";
  else if (n >= 7) tone = "blue";
  return <Badge tone={tone}>{n} ngày</Badge>;
}

export function NetBadge({ value }) {
  const n = Number(value) || 0;
  if (n > 0) return <Badge tone="emerald">Dương</Badge>;
  if (n < 0) return <Badge tone="red">Âm</Badge>;
  return <Badge tone="slate">Hòa</Badge>;
}

export function ReportPageHeader({
  eyebrow,
  title,
  description,
  scopeLabel,
  fromDate,
  toDate,
  extraBadges = [],
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl sm:rounded-[1.75rem] border border-red-100 bg-gradient-to-br from-red-700 via-red-700 to-red-900 p-4 sm:p-6 text-white shadow-lg shadow-red-100">
      <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-10 left-1/3 h-32 w-32 rounded-full bg-amber-300/20 blur-2xl" />
      <div className="relative">
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <Badge tone="red" className="!border-white/20 !bg-white/15 !text-white">
            {eyebrow}
          </Badge>
          {scopeLabel ? (
            <Badge tone="red" className="!border-white/20 !bg-white/10 !text-white/90">
              <Shield size={11} /> {scopeLabel}
            </Badge>
          ) : null}
          {fromDate && toDate ? (
            <Badge tone="red" className="!border-white/20 !bg-white/10 !text-white/90">
              <CalendarRange size={11} /> {dateVN(fromDate)} – {dateVN(toDate)}
            </Badge>
          ) : null}
          {extraBadges}
        </div>
        <h1 className="mt-2.5 sm:mt-3 text-xl sm:text-2xl font-black tracking-tight">
          {title}
        </h1>
        <p className="mt-1 sm:mt-1.5 max-w-2xl text-xs sm:text-sm text-white/80 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

export function SummaryStatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "emerald",
  badge,
}) {
  const tones = {
    emerald: {
      card: "border-emerald-100 from-emerald-50/80 to-white",
      icon: "bg-emerald-600 text-white shadow-emerald-100",
      value: "text-emerald-800",
    },
    red: {
      card: "border-red-100 from-red-50/80 to-white",
      icon: "bg-red-700 text-white shadow-red-100",
      value: "text-red-800",
    },
    blue: {
      card: "border-blue-100 from-blue-50/80 to-white",
      icon: "bg-blue-600 text-white shadow-blue-100",
      value: "text-blue-800",
    },
    amber: {
      card: "border-amber-100 from-amber-50/80 to-white",
      icon: "bg-amber-500 text-white shadow-amber-100",
      value: "text-amber-800",
    },
  };
  const t = tones[tone] || tones.emerald;

  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br p-3.5 sm:p-5 shadow-sm ${t.card}`}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-gray-400">
            {label}
          </div>
          <div
            className={`mt-1.5 sm:mt-2 text-lg sm:text-2xl font-black tabular-nums leading-tight break-all ${t.value}`}
          >
            {value}
          </div>
          {hint ? (
            <div className="mt-1 text-[11px] sm:text-xs text-gray-500 line-clamp-2">
              {hint}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1.5 sm:gap-2 shrink-0">
          <div className={`rounded-xl sm:rounded-2xl p-2 sm:p-2.5 shadow-md ${t.icon}`}>
            <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
          </div>
          {badge}
        </div>
      </div>
    </div>
  );
}

/** Lưới KPI: 2 cột mobile, desktop giữ grid đầy đủ */
export function SummaryStatGrid({ children }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:gap-4 xl:grid-cols-3">
      {children}
    </div>
  );
}

export function SectionCard({ title, countLabel, children, action }) {
  return (
    <div className="overflow-hidden rounded-2xl sm:rounded-[1.5rem] border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/40 px-3.5 sm:px-5 py-3 sm:py-3.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-sm font-black text-gray-900 truncate">{title}</div>
          {countLabel ? <Badge tone="gray">{countLabel}</Badge> : null}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Danh sách card cho bảng báo cáo trên mobile */
export function MobileReportCardList({ children, empty }) {
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return empty || null;
  }
  return (
    <div className="md:hidden divide-y divide-gray-100">{children}</div>
  );
}

export function MobileReportCard({
  title,
  subtitle,
  amount,
  amountTone = "text-gray-900",
  badges,
  meta,
}) {
  return (
    <div className="px-3.5 py-3.5 active:bg-gray-50/80">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">{title}</p>
          {subtitle ? (
            <p className="mt-0.5 text-xs text-gray-500 truncate">{subtitle}</p>
          ) : null}
          {badges ? (
            <div className="mt-2 flex flex-wrap gap-1.5">{badges}</div>
          ) : null}
          {meta ? (
            <p className="mt-1.5 text-[11px] text-gray-400 truncate">{meta}</p>
          ) : null}
        </div>
        {amount != null ? (
          <p
            className={`shrink-0 text-sm font-black tabular-nums ${amountTone}`}
          >
            {amount}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-8 sm:p-12 text-center">
      <div className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-2xl sm:rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50 text-gray-300">
        <Wallet size={20} className="sm:w-[22px] sm:h-[22px]" />
      </div>
      <p className="text-[11px] sm:text-xs font-black uppercase tracking-widest text-gray-400 px-4">
        {message}
      </p>
    </div>
  );
}
